import { useEffect, useMemo, useState } from 'react';
import {
  useEstablishmentForm,
  EstablishmentFormValues,
} from '../../../hooks/useEstablishmentForm';
import { useEstablishmentStore } from '@/store/EstablishmentStore/EstablishmentStore';
import { useToast } from '../../../hooks/useToast';
import { Options } from '@/components/Common/Selects/Select.types';
import { useGetSchoolsSimple } from '@/api/hooks/useSchool';
import { useGetRegions } from '@/api/hooks/useRegion';
import {
  SchoolOutput,
  CreateSchoolInput,
  SchoolType,
  SchoolCategory,
} from '@/api/gql/graphql';
import { useCreateSchool, useUpdateSchool } from '@/api/hooks';

export const useEstablishmentFormLogic = () => {
  const {
    openEstablishmentModal,
    setOpenEstablishmentModal,
    closeEstablishmentEditModal,
    isOpenEstablishmentEditModal,
    editingEstablishment,
  } = useEstablishmentStore();
  const { showToast } = useToast();
  const { handleSubmit, register, errors, reset, setValue, watch } =
    useEstablishmentForm();
  const { mutate: createSchool, isPending: isCreatingSchool } =
    useCreateSchool();
  const { mutate: updateSchool, isPending: isUpdatingSchool } =
    useUpdateSchool();

  // Déterminer le mode : création ou mise à jour
  const isEditMode = !!editingEstablishment;
  const isSubmitting = isCreatingSchool || isUpdatingSchool;

  const [schoolSearchQuery, setSchoolSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<SchoolOutput | null>(
    null,
  );
  const [isExistingSchool, setIsExistingSchool] = useState(false);
  const [hasUserFinishedTyping, setHasUserFinishedTyping] = useState(false);
  const [selectedEstablishmentType, setSelectedEstablishmentType] =
    useState<Options | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Options | null>(
    null,
  );
  const [selectedRegion, setSelectedRegion] = useState<Options | null>(null);

  const establishmentNameValue = watch('establishmentName');

  const establishmentTypeOptions: Options[] = [
    { _id: '1', name: 'Public' },
    { _id: '2', name: 'Privé' },
  ];

  const categoryOptions: Options[] = [
    { _id: SchoolCategory.Professional, name: 'Professionnel' },
    { _id: SchoolCategory.Secondary, name: 'Secondaire' },
    { _id: SchoolCategory.University, name: 'Universitaire' },
  ];

  // Debounce de la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(schoolSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [schoolSearchQuery]);

  const { data: schools } = useGetSchoolsSimple({
    search: debouncedSearchQuery,
  });

  const { data: regions } = useGetRegions();

  // Pré-remplir le formulaire en mode édition - inspiré de QuizzForm
  useEffect(() => {
    const dataToUse = editingEstablishment;

    if (isEditMode && dataToUse) {
      setValue('establishmentName', dataToUse.name || '');
      setValue('address', dataToUse.address || '');
      setValue('creditsIA', dataToUse.iaCredit || 0);

      // Pré-remplir le type d'établissement - approche similaire à QuizzForm
      const typeValue = dataToUse.type === 'PRIVATE' ? '2' : '1';
      const typeName = dataToUse.type === 'PRIVATE' ? 'Privé' : 'Public';

      setValue('establishmentType', typeValue);

      // Définir l'option sélectionnée comme dans QuizzForm
      setSelectedEstablishmentType({ _id: typeValue, name: typeName });

      if (dataToUse.contact) {
        const fullName =
          `${dataToUse.contact.firstName || ''} ${dataToUse.contact.lastName || ''}`.trim();
        setValue('responsibleName', fullName);
        setValue('responsibleEmail', dataToUse.contact.email || '');
        setValue('establishmentNumber', dataToUse.contact.phone || '');
      }

      // Pré-remplir la région si elle existe
      if (dataToUse.region) {
        const regionId = dataToUse.region._id || '';
        setValue('region', regionId);
        setSelectedRegion({
          _id: regionId,
          name: dataToUse.region.name || '',
        });
      }

      // Pré-remplir la catégorie si elle existe
      if (dataToUse.categories && dataToUse.categories.length > 0) {
        const cat = dataToUse.categories[0];
        setValue('category', cat);
        const catOption = categoryOptions.find((o) => o._id === cat);
        if (catOption) setSelectedCategory(catOption);
      }
    } else {
      // Réinitialiser en mode création
      setSelectedEstablishmentType(null);
      setSelectedCategory(null);
      setSelectedRegion(null);
    }
  }, [isEditMode, editingEstablishment, setValue]);

  const regionOptions: Options[] = useMemo(() => {
    if (!regions) return [];
    return regions.map((region) => ({
      _id: region._id,
      name: region.name,
    }));
  }, [regions]);

  const handleCloseModal = () => {
    if (isEditMode) {
      closeEstablishmentEditModal();
    } else {
      setOpenEstablishmentModal(false);
    }
    reset();
    setValue('creditsIA', 0);
    setHasUserFinishedTyping(false);
    setIsExistingSchool(false);
    setSelectedSchool(null);
    setSelectedEstablishmentType(null);
    setSelectedCategory(null);
    setSelectedRegion(null);
  };

  const handleChangeEstablishmentType = (option: Options) => {
    setValue('establishmentType', option?._id?.toString() || '');
    setSelectedEstablishmentType(option);
  };

  const handleChangeRegion = (option: Options) => {
    setValue('region', option?._id?.toString() || '');
    setSelectedRegion(option);
  };

  const handleChangeCategory = (option: Options) => {
    setValue('category', option?._id?.toString() || '');
    setSelectedCategory(option);
  };

  // Normalisation pour comparer les noms d'écoles (insensible à la casse/accents)
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  // Logique de recherche d'école
  const searchSchool = (event: { query: string }) => {
    if (event.query.length >= 2) {
      setSchoolSearchQuery(event.query);
    } else {
      setSchoolSearchQuery('');
    }
  };

  const handleSchoolChange = (event: { value: string }) => {
    const input = event.value;
    setHasUserFinishedTyping(false);

    const existingSchool = schools?.find(
      (school) => normalize(school.name) === normalize(input),
    );

    if (existingSchool) {
      setSelectedSchool(existingSchool);
      setIsExistingSchool(true);
      if (existingSchool.region) {
        const regionId = existingSchool.region._id || '';
        setValue('region', regionId);
        setSelectedRegion({
          _id: regionId,
          name: existingSchool.region.name || '',
        });
      }
    } else {
      setSelectedSchool(null);
      setIsExistingSchool(false);
    }

    setValue('establishmentName', input);
  };

  const handleSchoolBlur = () => {
    const currentValue = establishmentNameValue?.trim();
    if (currentValue && currentValue.length > 0) {
      setHasUserFinishedTyping(true);
    }
  };

  // Suggestions enrichies (si on veut ajouter icônes, types, etc.)
  const schoolSuggestions = useMemo(() => {
    if (!schools) return [];
    return schools.map((school) => ({
      label: school.name,
      value: school.name,
      id: school._id,
      type: school.type,
    }));
  }, [schools]);

  // Fonction helper pour séparer le nom complet en firstName et lastName
  const parseFullName = (fullName: string) => {
    const trimmedName = fullName.trim();
    const parts = trimmedName.split(' ');

    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }

    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');

    return { firstName, lastName };
  };

  // Fonction pour mapper le type d'établissement vers les types acceptés par l'API
  const mapEstablishmentType = (typeId: string): SchoolType => {
    return typeId === '2' ? SchoolType.Private : SchoolType.Public;
  };

  const onSubmit = async (data: EstablishmentFormValues) => {
    try {
      const trimmedName = data.establishmentName?.trim();

      if (!trimmedName) {
        showToast("Le nom de l'établissement est requis.", 'error');
        return;
      }

      // Vérifier que la région est sélectionnée pour les nouvelles écoles
      if (!isExistingSchool && hasUserFinishedTyping && !data.region) {
        showToast('La région est requise pour une nouvelle école.', 'error');
        return;
      }

      // Séparer le nom du responsable en firstName et lastName
      const { firstName, lastName } = parseFullName(data.responsibleName);

      // Préparer les données pour l'API CreateSchool
      const createSchoolInput: CreateSchoolInput = {
        name:
          isExistingSchool && selectedSchool ? selectedSchool._id : trimmedName,
        address: data.address,
        contact: {
          firstName,
          lastName,
          email: data.responsibleEmail,
          phone: data.establishmentNumber,
        },
        iaCredit: data.creditsIA,
        type: mapEstablishmentType(data.establishmentType),
        categories: [data.category] as SchoolCategory[],
        ...(data.region && { region: data.region }),
      };

      const idForUpdate = editingEstablishment?._id;

      if (isEditMode && idForUpdate) {
        // Mode mise à jour
        updateSchool(
          { id: idForUpdate, input: createSchoolInput },
          {
            onSuccess: () => {
              showToast('Établissement mis à jour avec succès', 'success');
              handleCloseModal();
            },
            onError: (error) => {
              showToast(error.message, 'error');
            },
          },
        );
      } else {
        // Mode création
        createSchool(createSchoolInput, {
          onSuccess: () => {
            showToast('Établissement ajouté avec succès', 'success');
            handleCloseModal();
          },
          onError: (error) => {
            showToast(error.message, 'error');
          },
        });
      }
    } catch (error) {
      showToast("Erreur lors de l'ajout de l'établissement", 'error');
    }
  };

  return {
    // États
    openEstablishmentModal,
    isOpenEstablishmentEditModal,
    isSubmitting,
    isEditMode,

    // Données
    establishmentTypeOptions,
    regionOptions,
    schoolSuggestions,
    establishmentNameValue,
    selectedSchool,
    isExistingSchool,
    hasUserFinishedTyping,
    selectedEstablishmentType,
    selectedCategory,
    selectedRegion,
    categoryOptions,

    // Formulaire
    handleSubmit,
    register,
    errors,
    setValue,
    watch,

    // Handlers
    handleCloseModal,
    handleChangeEstablishmentType,
    handleChangeRegion,
    handleChangeCategory,
    searchSchool,
    handleSchoolChange,
    handleSchoolBlur,
    onSubmit,
  };
};
